import React, { useEffect, useState } from 'react';

import { search } from 'flow/actions/app-actions';
import {
    fetchChatSessions,
    fetchPublicChatSessions,
    updatePublicMessageLoading,
} from 'flow/actions/chat-actions';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { selectHasChatSessions } from 'flow/selectors/chat-selectors';
import { selectHasThreads } from 'flow/selectors/feature-flag-selectors';
import { selectHasSearchText } from 'flow/selectors/selectors';
import ChatSessionsUnfilteredList from './chat-sessions-list/ChatSessionsUnfilteredList';
import ChatSessionsSearchList from './chat-sessions-list/ChatSessionsSearchList';

/** Fetches the chat sessions and hands over to whichever list belongs on
 *  screen. Searching narrows the chat rows and adds a section of colleagues to
 *  start a chat with, so the two lists are built from different state. */
const ChatSessionsList = () => {
    const dispatch = useAppDispatch();

    const [isLoadedChat, setIsLoadedChat] = useState(false);

    const isSearching = useAppSelector(selectHasSearchText);

    const hasChatSessions = useAppSelector(selectHasChatSessions);
    const isThreadEnabled = useAppSelector(selectHasThreads);

    /* Feature flags are fetched at app start, so this can run before the answer
     * has arrived and again once it has - the sessions are then refetched with
     * threads included. */
    useEffect(() => {
        const chatSessionIncludes = isThreadEnabled
            ? ['contact', 'threads']
            : ['contact'];

        const onUpdatePubMessageLoading = (status = false) =>
            dispatch(updatePublicMessageLoading(status));

        dispatch(fetchChatSessions(...chatSessionIncludes)).finally(() =>
            setIsLoadedChat(true),
        );

        onUpdatePubMessageLoading(true);
        dispatch(fetchPublicChatSessions()).finally(() =>
            onUpdatePubMessageLoading(false),
        );
    }, [dispatch, isThreadEnabled]);

    useEffect(() => {
        return () => {
            dispatch(search(''));
        };
    }, [dispatch]);

    /* Once there are chats, the skeleton has nothing left to stand in for.
     * This used to hang off the sessions map's identity, which only worked
     * because the map was rewritten constantly. */
    useEffect(() => {
        if (hasChatSessions) {
            setIsLoadedChat(true);
        }
    }, [hasChatSessions]);

    return isSearching ? (
        <ChatSessionsSearchList />
    ) : (
        <ChatSessionsUnfilteredList isLoaded={isLoadedChat} />
    );
};

export default ChatSessionsList;
